import { useState, useCallback, useEffect } from 'react';

/**
 * Generic localStorage-backed clipboard hook.
 * Persists across page refreshes and is shared across browser tabs (same origin).
 * Updates in real time across all instances on the same page via a synthetic storage event.
 *
 * @param {string} storageKey - Unique localStorage key for this clipboard
 * @returns {{ clipboard: object | null, copyToClipboard: (item: object) => void }}
 */
const useClipboard = (storageKey) => {
  const [clipboard, setClipboard] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== storageKey) return;
      try {
        setClipboard(event.newValue ? JSON.parse(event.newValue) : null);
      } catch {
        setClipboard(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const copyToClipboard = useCallback((item) => {
    const entry = JSON.parse(JSON.stringify(item));
    localStorage.setItem(storageKey, JSON.stringify(entry));
    // Dispatch a synthetic storage event so other instances on the same page update immediately.
    // (Browsers only fire the native storage event in other tabs, not the current one.)
    window.dispatchEvent(new StorageEvent('storage', { key: storageKey, newValue: JSON.stringify(entry) }));
  }, [storageKey]);

  return { clipboard, copyToClipboard };
};

export default useClipboard;
